import { useMutation, MutationOptions } from '@tanstack/react-query';

import {
  deleteV1BusinessPartnerCurrentIcon,
  postV1BusinessPartnerCurrentIcon,
  putV1BusinessPartnerCurrent,
  putV1IdentityCurrent,
  type BusinessPartnerBody,
  type BusinessPartnerPatchBody,
} from '../../api/generated/pap';
import {
  putV1BusinessPartnerCurrentChange,
  type BusinessPartnerChangeBody,
} from '../../api/generated/pm';
import { getEtag } from '../../utils/etag';
import {
  invalidateCurrentBusinessPartnerWithIdentityInfo,
  invalidateCurrentIdentity,
} from '../../hooks/queries/appSession.queries';
import {
  invalidateCurrentIdentityRequestBusinessPartners,
  invalidateIdentityCurrentRequests,
  invalidateInvitesToJoin,
} from '../Companies/companies.queries';
import {
  invalidateCurrentBusinessPartnerDivisionRequests,
  invalidateCurrentBusinessPartnerDivisions,
} from '../Divisions/divisions.queries';
import { invalidateCurrentChangeOwnershipRequest } from '../RequestChangeOwnership/changeOwnership.queries';
import { invalidateSuggestIdentities } from '../UserManagement/Users/invitation.queries';
import { invalidateUsers } from '../UserManagement/userManagement.queries';
import {
  invalidateCurrentBusinessPartner,
  invalidateCurrentBusinessPartnerChangePM,
  invalidateCurrentBusinessPartnerPM,
  invalidateCurrentIdentityBusinessPartners,
} from './businessPartner.queries';

/**
 * Mutation hooks for the current Business Partner's settings. The PUTs hit the
 * same path their own GET reads, so the interceptor attaches `If-Match` from
 * that GET's stored ETag. The icon POST/DELETE path has no GET, so no ETag is
 * ever captured for it and no `If-Match` is sent (the legacy helper sent an
 * empty one). Only `useDeactivateAccount` passes the header by hand (see
 * below). See `./IdentityProviders/identityProviders.mutations.ts` for the
 * pattern.
 */

/**
 * Everything cached under the current Business Partner, which a change to it
 * or to its icon makes stale.
 */
const invalidateCurrentBusinessPartnerReads = () => {
  invalidateCurrentBusinessPartner();
  invalidateCurrentBusinessPartnerPM();
  invalidateSuggestIdentities();
  invalidateCurrentBusinessPartnerDivisions();
  invalidateCurrentBusinessPartnerDivisionRequests();
  invalidateCurrentBusinessPartnerChangePM();
  invalidateCurrentChangeOwnershipRequest();
  invalidateCurrentBusinessPartnerWithIdentityInfo();
};

export const useUpdateCurrentBusinessPartner = (
  options?: Omit<
    MutationOptions<BusinessPartnerBody, Error, BusinessPartnerPatchBody>,
    'mutationFn'
  >
) =>
  useMutation({
    ...options,
    mutationFn: async (payload: BusinessPartnerPatchBody) => {
      const { data } = await putV1BusinessPartnerCurrent({ body: payload, throwOnError: true });
      return data;
    },
    onSuccess: (...args) => {
      invalidateCurrentBusinessPartnerReads();
      invalidateUsers();
      options?.onSuccess?.(...args);
    },
  });

export const useUpdateCurrentBusinessPartnerPM = () =>
  useMutation({
    mutationFn: async (payload: BusinessPartnerChangeBody) => {
      await putV1BusinessPartnerCurrentChange({ body: payload, throwOnError: true });
    },
  });

export const useUpdateBusinessPartnerIcon = (
  options?: Omit<MutationOptions<unknown, Error, Blob>, 'mutationFn'>
) =>
  useMutation({
    ...options,
    mutationFn: async (blob: Blob) => {
      // The cropped logo arrives as a bare Blob; name the multipart part as the
      // legacy upload did so the wire body stays the same.
      const { data } = await postV1BusinessPartnerCurrentIcon({
        body: { thumbnail: new File([blob], 'logo.png', { type: blob.type }) },
        throwOnError: true,
      });
      return data;
    },
  });

export const useDeleteBusinessPartnerIcon = (
  options?: Omit<MutationOptions<unknown, Error>, 'mutationFn'>
) =>
  useMutation({
    ...options,
    mutationFn: async () => {
      const { data } = await deleteV1BusinessPartnerCurrentIcon({ throwOnError: true });
      return data;
    },
    onSuccess: (...args) => {
      invalidateCurrentBusinessPartnerReads();
      options?.onSuccess?.(...args);
    },
  });

export const useDeactivateAccount = () =>
  useMutation({
    mutationFn: async () => {
      // The identity's ETag comes from `GET /v1/identity/current?external=true`,
      // a different query string from this PUT, so the shared interceptor can't
      // supply it automatically. Read the etag captured from that GET and pass
      // it explicitly.
      const etag = getEtag('/api/ciam/v1/identity/current?external=true');
      const { data } = await putV1IdentityCurrent({
        body: { archived: true },
        ...(etag ? { headers: { 'If-Match': etag } } : {}),
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      invalidateCurrentIdentity();
      invalidateCurrentIdentityBusinessPartners();
      invalidateCurrentIdentityRequestBusinessPartners();
      invalidateInvitesToJoin();
      invalidateIdentityCurrentRequests();
    },
  });
